/**
 * Figma `185:5432`'s "Consumo de créditos" chart and "Consumo por campanha" list, both pure and
 * presentational like `CapacityStats` — `Capacity.tsx` fetches with `useConsumption` and passes
 * the result straight through. The frame also draws a daily-limit reference line and a "days over
 * the limit" foot stat; neither `core` nor `api` model a per-tenant daily credit limit anywhere
 * (`fs-campaign-capacity-api-credits-consumption-series`'s `ConsumptionOut` carries only `daily`
 * and `by_campaign`), so both are left out rather than invented — flagged as an owner-decision gap
 * in this task's report rather than a silent value.
 *
 * The frame's "Consumo por campanha" list also shows a zero-consumption campaign; `by_campaign`
 * only ever lists a campaign with a debit in the window ("a zero row would say nothing a missing
 * one does not" — `ConsumptionOut`'s own docstring), and reconstructing the full campaign roster
 * here to backfill zero rows would be a second, ungoverned join `OverviewStatGrid`'s own comment
 * already warns against. Rendered as the route actually answers: busiest first, no zero rows.
 */

import type { ReactNode } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis } from 'recharts';

import { Absent } from '../../../chrome/measure';
import { RankRow } from '../../../chrome/chart';
import { EmptyState } from '../../../chrome/state';
import { Card, CardContent, CardHeader } from '../../../ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../ui/chart';
import { getLocale, type Locale } from '../../../i18n';
import { credits as formatCredits } from '../../../i18n/format';
import type { CampaignConsumption, DailyConsumption } from './api/schemas';
import { cm } from './messages';

function dayLabel(date: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(`${date}T00:00:00Z`),
  );
}

function shortDate(date: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
}

/** A bar's fill as a fraction of the row list's own longest value — geometry only, no figure on
 *  screen is this number. Same shape as `data/overview/figures.ts`'s `fractionsOf`, kept local: a
 *  cross-feature import for three lines is a worse trade than the duplication. */
function sharesOf(values: readonly number[]): number[] {
  const max = Math.max(0, ...values);
  return values.map((value) => (max === 0 ? 0 : value / max));
}

export function ConsumptionChart(props: { daily: DailyConsumption[] }): ReactNode {
  const { daily } = props;
  const locale = getLocale();
  const config: ChartConfig = {
    credits: { label: cm('consumption.series'), color: 'var(--chart-1)' },
  };
  const total = daily.reduce((sum, day) => sum + day.credits, 0);
  const average = daily.length === 0 ? null : Math.round(total / daily.length);
  const maxDay = daily.reduce<DailyConsumption | null>(
    (max, day) => (max === null || day.credits > max.credits ? day : max),
    null,
  );

  return (
    <Card>
      <CardHeader className="flex flex-col gap-1">
        <h2 className="text-title-sm">{cm('consumption.heading')}</h2>
        <p className="text-caption text-muted-foreground">{cm('consumption.window')}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <ChartContainer
          config={config}
          role="img"
          aria-label={cm('consumption.label')}
          className="aspect-auto h-64 w-full"
        >
          {/* The container above is the accessible image; recharts' own layer would put a second,
              contradictory `role="application"` tab stop inside it, named by the run-together tick
              labels. Same pairing as `../../data/sentiment/NcsSeries.tsx`. */}
          <BarChart data={daily} accessibilityLayer={false}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: string) => dayLabel(value, locale)}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(_value, payload) => {
                    const date = (payload[0]?.payload as DailyConsumption | undefined)?.date;
                    return date === undefined ? '' : shortDate(date, locale);
                  }}
                />
              }
            />
            <Bar dataKey="credits" fill="var(--color-credits)" radius={4} />
          </BarChart>
        </ChartContainer>
        <dl className="grid grid-cols-2 gap-4 border-t pt-4">
          <div className="flex flex-col gap-1">
            <dt className="text-caption text-muted-foreground">{cm('consumption.average')}</dt>
            <dd data-numeric className="text-body-sm">
              {average === null ? <Absent reason="not_measured" /> : formatCredits(average, locale)}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="text-caption text-muted-foreground">{cm('consumption.maxDay')}</dt>
            <dd data-numeric className="text-body-sm">
              {maxDay === null ? (
                <Absent reason="not_measured" />
              ) : (
                cm('consumption.maxDay.value', {
                  value: formatCredits(maxDay.credits, locale),
                  date: shortDate(maxDay.date, locale),
                })
              )}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

export function ConsumptionByCampaign(props: { byCampaign: CampaignConsumption[] }): ReactNode {
  const { byCampaign } = props;
  const locale = getLocale();
  const shares = sharesOf(byCampaign.map((row) => row.credits));

  return (
    <Card>
      <CardHeader className="flex flex-col gap-1">
        <h2 className="text-title-sm">{cm('consumption.byCampaign.heading')}</h2>
        <p className="text-caption text-muted-foreground">{cm('consumption.window')}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {byCampaign.length === 0 ? (
          <EmptyState title={cm('consumption.byCampaign.empty')} />
        ) : (
          byCampaign.map((row, index) => (
            <RankRow
              key={row.campaign_id}
              label={row.campaign_name}
              value={formatCredits(row.credits, locale)}
              share={shares[index]}
              tone="positive"
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}
